import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  FileText,
  Plus,
  Sparkles,
  Star,
  Trash2,
  Upload,
  Wrench,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { DocxPreview } from "@/components/DocxPreview";
import { downloadBase64File, readFileAsBase64 } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function SettingsContractTemplatesPage() {
  const utils = trpc.useUtils();
  const list = trpc.contractTemplates.list.useQuery();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadIsDefault, setUploadIsDefault] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const preview = trpc.contractTemplates.previewHtml.useQuery(
    { id: previewId ?? "" },
    { enabled: !!previewId, staleTime: 60_000 }
  );

  const create = trpc.contractTemplates.create.useMutation({
    onSuccess: () => {
      utils.contractTemplates.list.invalidate();
      setUploadOpen(false);
      setUploadName("");
      setUploadDescription("");
      setUploadFile(null);
      setUploadIsDefault(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Contract template uploaded");
    },
    onError: (e) => toast.error(e.message ?? "Upload failed"),
  });

  const setDefault = trpc.contractTemplates.setDefault.useMutation({
    onSuccess: () => {
      utils.contractTemplates.list.invalidate();
      toast.success("Default template updated");
    },
    onError: (e) => toast.error(e.message ?? "Could not set default"),
  });

  const remove = trpc.contractTemplates.delete.useMutation({
    onSuccess: () => {
      utils.contractTemplates.list.invalidate();
      setDeleteId(null);
      toast.success("Deleted");
    },
    onError: (e) => toast.error(e.message ?? "Delete failed"),
  });

  const update = trpc.contractTemplates.update.useMutation({
    onSuccess: () => {
      utils.contractTemplates.list.invalidate();
      toast.success("Updated");
    },
    onError: (e) => toast.error(e.message ?? "Update failed"),
  });

  const buildTemplate = trpc.contractTemplates.buildTemplate.useMutation({
    onMutate: (vars) => {
      setBuildingId(vars.id);
    },
    onSuccess: (res, vars) => {
      utils.contractTemplates.list.invalidate();
      // The build rewrites the stored .docx, so the cached previews
      // (PDF + HTML) for this template are now stale — drop them so the
      // preview dialog rebuilds from the freshly templated file.
      utils.contractTemplates.previewPdf.invalidate({ id: vars.id });
      utils.contractTemplates.previewHtml.invalidate({ id: vars.id });
      const detected = res.detectedVariables ?? [];
      toast.success(
        `Template built — ${res.replacementsCount} replacement(s), ${detected.length} variable(s) detected`,
        { description: res.notes || undefined }
      );
    },
    onError: (e) => toast.error(e.message ?? "Build failed"),
    onSettled: () => {
      setBuildingId(null);
    },
  });

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error("Choose a .docx file");
      return;
    }
    const trimmedName = uploadName.trim() || uploadFile.name.replace(/\.docx$/i, "");
    try {
      const base64 = await readFileAsBase64(uploadFile);
      create.mutate({
        name: trimmedName,
        description: uploadDescription.trim() || null,
        fileName: uploadFile.name,
        fileBase64: base64,
        isDefault: uploadIsDefault,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not read file");
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const data = await utils.contractTemplates.download.fetch({ id });
      downloadBase64File(data.contentBase64, data.fileName, data.mimeType);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const items = list.data ?? [];

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Contract templates
          </h1>
          <p className="text-sm text-muted-foreground">
            Upload Word (.docx) templates. When an opportunity moves into the
            "Contract signing" stage, a contract will be generated automatically
            from the default template and attached to the opportunity and client.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4" />
          Upload template
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="prose prose-sm max-w-none py-4 text-sm">
          <p className="font-medium">Use these variables inside your .docx:</p>
          <p className="text-xs text-muted-foreground">
            For example: <code>{"{client.company_name}"}</code>,{" "}
            <code>{"{company.legal_name}"}</code>,{" "}
            <code>{"{pricing.monthly_total_eur}"}</code>. Loop through products:{" "}
            <code>{"{#products}{name} — {qty} × {unit_amount_eur}{/products}"}</code>.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Tip: upload a real, filled-in contract and click{" "}
            <span className="font-medium">Build template</span> — AI will replace
            literal party names, codes, amounts and dates with the right
            placeholders.
          </p>
          <ul className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
            <li><code>company.legal_name</code></li>
            <li><code>company.reg_code</code></li>
            <li><code>company.vat_code</code></li>
            <li><code>company.address_line</code>, <code>company.city</code></li>
            <li><code>company.signatory_name</code></li>
            <li><code>company.signatory_title</code></li>
            <li><code>company.bank_name</code>, <code>company.bank_account</code></li>
            <li><code>client.company_name</code></li>
            <li><code>client.reg_code</code>, <code>client.vat_code</code></li>
            <li><code>client.address_line</code></li>
            <li><code>client.contact_name</code>, <code>client.contact_email</code></li>
            <li><code>opportunity.name</code></li>
            <li><code>pricing.monthly_total_eur</code></li>
            <li><code>pricing.annual_total_eur</code></li>
            <li><code>pricing.seat_plan_name</code>, <code>pricing.seats_total</code></li>
            <li><code>pricing.seats_included</code>, <code>pricing.per_seat_eur</code></li>
            <li><code>products[]</code> (loop), <code>main_products</code>, <code>extra_products</code></li>
            <li><code>date.lt</code>, <code>date.iso</code>, <code>date.year</code></li>
          </ul>
        </CardContent>
      </Card>

      {list.isLoading && (
        <div className="grid gap-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      )}

      {list.isError && (
        <Card>
          <CardContent className="py-6 text-center text-sm text-destructive">
            {list.error.message}
          </CardContent>
        </Card>
      )}

      {!list.isLoading && items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="font-medium">No contract templates yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                The first uploaded template automatically becomes the default.
              </p>
            </div>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" />
              Upload first
            </Button>
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((t) => {
            const isBuilding = buildingId === t.id && buildTemplate.isPending;
            return (
              <Card key={t.id} className={cn(!t.isActive && "opacity-60")}>
                <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setPreviewId(t.id)}
                    className="min-w-0 flex-1 cursor-pointer rounded-md text-left hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring -mx-2 px-2 py-1 transition-colors"
                    aria-label={`Preview template ${t.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate text-sm font-medium">
                        {t.name}
                      </span>
                      {t.isDefault && (
                        <Badge className="gap-1 text-xs">
                          <Star className="h-3 w-3 fill-current" /> Default
                        </Badge>
                      )}
                      {!t.isActive && (
                        <Badge variant="secondary" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {t.description && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t.description}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{t.fileName}</span>
                      {" · "}
                      {formatBytes(t.fileSize)}
                      {" · v"}
                      {t.version}
                      {t.createdAt && (
                        <>
                          {" · Uploaded "}
                          {new Date(t.createdAt).toLocaleDateString("en-US")}
                        </>
                      )}
                    </p>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    {t.variablesSchema ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => buildTemplate.mutate({ id: t.id })}
                        disabled={isBuilding}
                        title="Re-run AI to update template placeholders"
                      >
                        <Wrench className="h-3.5 w-3.5" />
                        {isBuilding ? "Fixing…" : "Fix template"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => buildTemplate.mutate({ id: t.id })}
                        disabled={isBuilding}
                        title="Run AI to replace literal values with template placeholders"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        {isBuilding ? "Building…" : "Build template"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(t.id)}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </Button>
                    <Button
                      variant={t.isDefault ? "secondary" : "outline"}
                      size="sm"
                      disabled={t.isDefault || setDefault.isPending}
                      onClick={() => setDefault.mutate({ id: t.id })}
                    >
                      <Star
                        className={cn("h-3.5 w-3.5", t.isDefault && "fill-current")}
                      />
                      {t.isDefault ? "Default" : "Make default"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        update.mutate({
                          id: t.id,
                          patch: { isActive: !t.isActive },
                        })
                      }
                    >
                      {t.isActive ? "Disable" : "Enable"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(t.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload contract template</DialogTitle>
            <DialogDescription>
              Pick a Word (.docx) file. Use{" "}
              <code>{"{client.company_name}"}</code>-style placeholders, or
              upload a filled-in contract and run <em>Build template</em>{" "}
              afterwards.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g. Standard SaaS agreement"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Word file (.docx)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                className="mt-1"
                required
              />
              {uploadFile && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {uploadFile.name} · {formatBytes(uploadFile.size)}
                </p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={uploadIsDefault}
                onChange={(e) => setUploadIsDefault(e.target.checked)}
                className="h-4 w-4"
              />
              Make default template
            </label>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={create.isPending || !uploadFile}
              >
                {create.isPending ? "Uploading…" : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!previewId}
        onOpenChange={(o) => !o && setPreviewId(null)}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {preview.data?.name ?? "Template preview"}
            </DialogTitle>
            <DialogDescription>
              {preview.data?.fileName
                ? `${preview.data.fileName} · Word (.docx) preview. Template placeholders such as {client.legal_name} appear as literal text.`
                : "Word (.docx) preview."}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[75vh] overflow-hidden rounded-md border bg-muted">
            {preview.isLoading && (
              <div className="space-y-2 p-6">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-full" />
              </div>
            )}
            {preview.isError && (
              <p className="p-6 text-sm text-destructive">
                {preview.error.message}
              </p>
            )}
            {preview.data && (
              <DocxPreview
                html={preview.data.html}
                className="h-full w-full overflow-auto"
              />
            )}
          </div>
          <DialogFooter>
            {previewId && (
              <Button
                variant="outline"
                onClick={() => handleDownload(previewId)}
              >
                <Download className="h-4 w-4" />
                Download .docx
              </Button>
            )}
            <Button onClick={() => setPreviewId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Previously generated contracts will not be
              affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => deleteId && remove.mutate({ id: deleteId })}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
